import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { getPublicProjects } from "../services/publicApi";
import { Link } from "react-router-dom";
import { BACKEND_URL } from "../util/api";

const Projects = ({ scroll = false }) => {
  const [projects, setProjects] = useState(
    []
  );

  const [loading, setLoading] = useState(
    false
  );

  const [loadingMore, setLoadingMore] = useState(false);

  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const observerRef = useRef(null);

  /*
   * Load first page
   */
  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await getPublicProjects(1, 6);

        if (result.success) {
          setProjects(result.data?.data || []);

          setCurrentPage(
            result.data?.current_page || 1
          );

          setLastPage(
            result.data?.last_page || 1
          );
        }
      } catch (err) {
        console.error(
          "Failed to load projects:",
          err
        );

        setError(
          err.message ||
          "Unable to load projects."
        );
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, []);

  /*
   * Load next page
   */
  const loadMoreProjects = useCallback(
    async () => {
      if (loadingMore) return;

      if (currentPage >= lastPage) {
        return;
      }

      try {
        setLoadingMore(true);

        const nextPage = currentPage + 1;

        const result =
          await getPublicProjects(
            nextPage,
            6
          );

        if (result.success) {
          const newProjects =
            result.data?.data || [];

          setProjects((previousProjects) => [
            ...previousProjects,
            ...newProjects,
          ]);

          setCurrentPage(
            result.data?.current_page ||
            nextPage
          );

          setLastPage(
            result.data?.last_page ||
            lastPage
          );
        }
      } catch (err) {
        console.error(
          "Failed to load more projects:",
          err
        );
      } finally {
        setLoadingMore(false);
      }
    },
    [
      currentPage,
      lastPage,
      loadingMore,
    ]
  );

  /*
   * Infinite scroll
   */
  useEffect(() => {
    const element = observerRef.current;

    if (!element) return;

    const observer =
      new IntersectionObserver(
        (entries) => {
          const firstEntry =
            entries[0];

          if (
            firstEntry.isIntersecting &&
            !loading &&
            !loadingMore &&
            currentPage < lastPage
          ) {
            loadMoreProjects();
          }
        },
        {
          root: null,

          // Start loading before the user
          // actually reaches the bottom.
          rootMargin: "300px",

          threshold: 0,
        }
      );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [
    loading,
    loadingMore,
    currentPage,
    lastPage,
    loadMoreProjects,
  ]);

  /*
   * Initial loading
   */
  if (loading) {
    return (
      <section
        className="projects-section section-padding"
        id="projects"
        aria-labelledby="projects-title"
      >
        <div className="container">
          <div className="projects-loading">
            Loading projects...
          </div>
        </div>
      </section>
    );
  }

  /*
   * Error
   */
  if (error) {
    return (
      <section
        className="projects-section section-padding"
        id="projects"
        aria-labelledby="projects-title"
      >
        <div className="container">
          <div className="projects-error">
            Unable to load projects at
            the moment.
          </div>
        </div>
      </section>
    );
  }

  /*
   * No projects
   */
  if (!projects.length) {
    return null;
  }

  return (
    <section
      className="projects-section section-padding"
      id="projects"
      aria-labelledby="projects-title"
    >
      <div className="container">

        {/* HEADER */}
        <div className="section-heading">

          <div>
            <p className="section-label">
              <span />
              PROJECTS &amp; INITIATIVES
            </p>

            <h2 id="projects-title">
              Development that{" "}
              <em>touches lives.</em>
            </h2>
          </div>

          {scroll === false &&

            <Link
              to="/projects"
              className="view-all"
            >
              View All Projects →
            </Link>
          }

        </div>

        {/* PROJECTS */}
        <div className="project-grid">

          {projects.map(
            (project, index) => (
              <article
                className="project-card"
                key={
                  project.id ||
                  `${project.title}-${index}`
                }
              >

                <div
                  className={`project-image project-${(index % 6) + 1
                    }`}
                  style={
                    project.image
                      ? {
                        backgroundImage:
                          `url(${ BACKEND_URL +"/storage/"+ project.image})`,
                          backgroundSize: "contain",
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                          position: "relative",
                      }
                      : undefined
                  }
                >

                    <div style={{
                                    position: "absolute", backgroundImage: `url(${BACKEND_URL + "/storage/" + project.image})`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center", filter: "blur(10px)",
                                    width: "100%", height: "100%",
                                     opacity: 0.2
                                  }}>
                  
                    </div> 



                  <span>
                    {project.tag ||
                      project.category ||
                      "PROJECT"}
                  </span>
                </div>

                <div className="project-content">

                  <small>
                    {String(
                      index + 1
                    ).padStart(2, "0")}{" "}
                    /{" "}
                    {project.tag ||
                      project.category ||
                      "PROJECT"}
                  </small>

                  <h3>
                    {project.title}
                  </h3>

                  {project.description && (
                    <p>
                      {project.description}
                    </p>
                  )}

                  <Link
                    to={
                      project.slug
                        ? `/projects/${project.slug}`
                        : "#projects"
                    }
                  >
                    Explore Initiative

                    <span
                      aria-hidden="true"
                    >
                      →
                    </span>
                  </Link>

                </div>

              </article>
            )
          )}

        </div>

        {/* INFINITE SCROLL TRIGGER */}
        {(currentPage < lastPage  && scroll === true) && (
          <div
            ref={observerRef}
            className="projects-load-more"
            aria-live="polite"
          >
            {loadingMore && (
              <>
                <span
                  className="projects-spinner"
                  aria-hidden="true"
                />

                <span>
                  Loading more projects...
                </span>
              </>
            )}
          </div>
        )}

        {/* END */}
        {currentPage >= lastPage &&
          projects.length > 0 && (
            <div className="projects-end">
              <span>
              {/*   You've reached the end of
                the projects. */}
              </span>
            </div>
          )}

      </div>
    </section>
  );
};

export default Projects;